import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { relTime, initials } from "../lib/format";

const QUICK = ["Ja tak", "Ja tak 2 stk", "jatak x3", "Hvad koster den?", "Annuller"];

function Thread({ thread }) {
  const ref = useRef();
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [thread]);
  if (!thread) return null;
  return (
    <article className="fb-post">
      <header>
        <span className="fb-avatar">{initials(thread.store_name)}</span>
        <div>
          <b>{thread.store_name}</b>
          <br />
          <small className="muted">{relTime(thread.published_at)}</small>
        </div>
      </header>
      <div className="fb-text">{thread.post_text}</div>
      {thread.image_path && <img className="fb-img" src={thread.image_path} alt="" />}
      <div className="fb-comments" id="fb-comments" ref={ref}>
        {thread.comments.length ? (
          thread.comments.map((c, i) => (
            <div key={i}>
              <div className="fb-c">
                <span className="fb-avatar user">{initials(c.name)}</span>
                <div>
                  <div className="fb-bubble">
                    <b>{c.name || "Ukendt"}</b>
                    {c.message}
                  </div>
                  <div className="fb-meta">
                    {relTime(c.created_at)}
                    {c.liked ? " · 👍 Butikken synes godt om" : ""}
                    {c.intent === "none" ? " · ignoreret" : ""}
                  </div>
                  {c.private.map((p, j) => (
                    <div className="dm" key={j}>
                      <b>📩 Privat besked til {(c.name || "").split(" ")[0]}:</b>
                      {"\n" + p}
                    </div>
                  ))}
                </div>
              </div>
              {c.public.map((r, j) => (
                <div className="fb-c reply" key={j}>
                  <span className="fb-avatar">{initials(thread.store_name)}</span>
                  <div>
                    <div className="fb-bubble">
                      <b>{thread.store_name}</b>
                      {r.text}
                    </div>
                    <div className="fb-meta">{relTime(r.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            Ingen kommentarer endnu. Skriv den første herunder.
          </p>
        )}
      </div>
    </article>
  );
}

export default function Simulator() {
  const [params, setParams] = useSearchParams();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [names, setNames] = useState([]);
  const [name, setName] = useState("Anna Jensen");
  const [message, setMessage] = useState("Ja tak");
  const [busy, setBusy] = useState(false);
  const offerParam = params.get("offer");

  const load = useCallback(() => {
    api.get("/simulator", { params: offerParam ? { offer: Number(offerParam) } : {} }).then((r) => setD(r.data));
  }, [offerParam]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/simulator/names").then((r) => {
      setNames(r.data.names);
      setName(r.data.names[0]);
    });
  }, []);

  if (!d) return <div className="loading">Indlæser…</div>;

  const sendComment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/simulator/comment", { offer_id: d.offer_id, name, message });
      if (r.data.thread) setD((s) => ({ ...s, thread: r.data.thread }));
      else if (r.data.flash) flash(r.data.flash, "warn");
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const travel = async (to) => {
    try {
      const r = await api.post("/simulator/time", { offer_id: d.offer_id, to });
      if (r.data.flash) flash(r.data.flash);
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const nextCustomer = () => {
    const i = names.indexOf(name);
    setName(names[(i + 1) % names.length]);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Simulator</h1>
          <p>
            Afprøv robotten uden Facebook: skriv kommentarer som forskellige kunder og se svarene. Tilbud slået op i
            testtilstand ender her og tæller ikke med i statistikken.
          </p>
        </div>
      </div>
      {!d.offers.length ? (
        <div className="panel empty">
          <p>Slå et tilbud op – i testtilstand ender det her.</p>
          <Link className="btn jatak" to="/offers/new">
            Opret tilbud
          </Link>
        </div>
      ) : (
        <div className="grid cols-main">
          <div id="sim-thread">
            <Thread thread={d.thread} />
          </div>
          <aside>
            <section className="panel">
              <div className="field">
                <label htmlFor="sim-offer">Tilbud</label>
                <select
                  id="sim-offer"
                  value={d.offer_id || ""}
                  onChange={(e) => setParams({ offer: e.target.value })}
                  data-testid="sim-offer-select"
                >
                  {d.offers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title}
                    </option>
                  ))}
                </select>
              </div>
              <form onSubmit={sendComment}>
                <div className="field">
                  <label htmlFor="sim-name">Kunde</label>
                  <input id="sim-name" list="sim-names" value={name} onChange={(e) => setName(e.target.value)} required data-testid="sim-name" />
                  <datalist id="sim-names">
                    {names.map((n) => (
                      <option value={n} key={n} />
                    ))}
                  </datalist>
                </div>
                <div className="field">
                  <label htmlFor="sim-msg">Kommentar</label>
                  <input id="sim-msg" value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="sim-message" />
                </div>
                <div className="actions" style={{ marginBottom: 12 }}>
                  {QUICK.map((t) => (
                    <button type="button" className="btn small" key={t} onClick={() => setMessage(t)}>
                      {t}
                    </button>
                  ))}
                </div>
                <div className="actions">
                  <button className="btn jatak" disabled={busy} data-testid="sim-send">
                    Send kommentar
                  </button>
                  <button className="btn" type="button" onClick={nextCustomer} title="Skift til næste testkunde">
                    Næste kunde
                  </button>
                </div>
              </form>
            </section>
            <section className="panel">
              <h2>Spol tiden frem</h2>
              <p className="muted" style={{ fontSize: 13 }}>
                Afprøv påmindelser og udeblivelser uden at vente.
              </p>
              <div className="actions">
                <button className="btn small" onClick={() => travel("pickup_open")}>
                  Afhentning åbner
                </button>
                <button className="btn small danger" onClick={() => travel("pickup_ended")}>
                  Afhentning slut
                </button>
              </div>
            </section>
            <section className="panel">
              <Link className="btn" style={{ width: "100%" }} to={`/offers/${d.offer_id}`}>
                Se ordrer på tilbuddet
              </Link>
            </section>
          </aside>
        </div>
      )}
    </>
  );
}
